import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  Box,
  VStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
} from '@chakra-ui/react'
import type { SingleValue, OnChangeValue } from 'react-select'
import { IKReactWindowedSelect } from '../../../../../ui-kit'

import type { InputFormProps as Props } from '../../../../interfaces'
import { getFieldsMapping, TypeBadge } from '../../../../../utils/mediaLibrary'
import OperatorMenu from '../../OperatorMenu'

const CustomMetadataSelectInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    customMetadataField,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({
          fieldName: expression.fieldName,
          customMetadataField,
        }).getInitialValue(newOperator)
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (
      newValue: string | number | boolean | Array<string | number | boolean>,
    ) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="0.5em">
        <VStack alignItems="stretch" width="full" spacing="0.5em">
          <OperatorMenu
            width="full"
            expression={expression}
            customMetadataField={customMetadataField}
            handleOperatorChange={handleOperatorChange}
          />

          {['IS', 'IS_NOT'].includes(expression.operator) &&
            !Array.isArray(expression.value) && (
              <Box width="full">
                <IKReactWindowedSelect
                  menuPlacement='auto'
                  ref={initialFocusRef}
                  variant="compact"
                  placeholder="Select a value"
                  isSearchable={true}
                  options={getFieldsMapping({
                    fieldName: expression.fieldName,
                    customMetadataField,
                  }).possibleValues?.map(val => ({
                    label: (
                      <>
                        {String(val)} <TypeBadge val={val} />
                      </>
                    ),
                    value: val,
                  }))}
                  value={
                    (expression.value || expression.value === false) &&
                    typeof expression.value !== 'object'
                      ? {
                          label: (
                            <>
                              {String(expression.value)}{' '}
                              <TypeBadge val={expression.value} />
                            </>
                          ),
                          value: expression.value,
                        }
                      : null
                  }
                  onChange={(
                    newVal: SingleValue<{
                      label: JSX.Element
                      value: string
                    }>,
                  ) => {
                    handleValueChange(newVal?.value ?? '')
                    setDirty(true)
                  }}
                />
              </Box>
            )}
          {['IS_ONE_OF', 'IS_NONE_OF'].includes(expression.operator) &&
            Array.isArray(expression.value) && (
              <Box width="full">
                <IKReactWindowedSelect
                  menuPlacement='auto'
                  ref={initialFocusRef}
                  variant="compact"
                  placeholder="Select values"
                  isSearchable={true}
                  isMulti
                  options={getFieldsMapping({
                    fieldName: expression.fieldName,
                    customMetadataField,
                  }).possibleValues?.map(val => ({
                    label: (
                      <>
                        {String(val)} <TypeBadge val={val} />
                      </>
                    ),
                    value: val,
                  }))}
                  value={
                    expression.value.map(val => ({
                      value: val,
                      label: (
                        <>
                          {String(val)} <TypeBadge val={val} />
                        </>
                      ),
                    })) ?? []
                  }
                  onChange={(
                    arr: OnChangeValue<
                      {
                        label: JSX.Element
                        value: string
                      },
                      true
                    >,
                  ) => {
                    const newVal = arr.map(val => val.value)
                    handleValueChange(newVal)
                    setDirty(true)
                  }}
                />
              </Box>
            )}
        </VStack>

        <ChakraFormErrorMessage width="full">{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default CustomMetadataSelectInputForm
